import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { ErrorResponse, GlobalResponseObject } from '@shahi-analytics/backend-utils';
import axios from 'axios';
import { In, Repository } from 'typeorm';
import configuration from '../../config/configuration';
import { LineEntity } from '../entities/line.entity';
import { OperationEntity } from '../entities/operation.entity';
import { OperatorEntity } from '../entities/operator.entity';
import { ReasonEntity } from '../entities/reason.entity';
import { StyleOperationsEntity } from '../entities/style-operations.entity';
import { StyleEntity } from '../entities/style.entity';
import { LineActualDayTrackEntity } from '../entities/transactions/line-actual-track.entity';
import { LineDownTimeEntity } from '../entities/transactions/line-down-time.entity';
import { LineEfficiencyDayTrackEntity } from '../entities/transactions/line-efficiency.entity';
import { LineDayPlanEntity } from '../entities/transactions/line-plan';
import { MigrationLogEntity } from '../entities/transactions/migration-log.entity';
import { OperatorEfficiencyDayTrackEntity } from '../entities/transactions/operator-efficiency-track.entity';
import { WorkstationEntity } from '../entities/workstation.entity';
import { DbNamesEnum } from './db-connection/db-names.enum';
import { ShahiDbConnectionsService } from './db-connection/shahi-db-connection';
import { AppTableNamesEnum } from './models/app-table-names.enum';
import { MigrationStatusEnum } from './models/migration-status.enum';
import { ReasonPriorityEnum } from './models/reason-priority-enum';
import { UnitDateHourRequest } from './models/unit-date-hour.request';
import { MigrationService } from './migration.service';
import { UnitEntity } from '../entities/unit.entity';
import { UnitDayHourEntity } from '../entities/unit-day-hours.entity';
import { UnitBreakEntity } from '../entities/unit-break.entity';
import { UnitDayBreakEntity } from '../entities/unit-day-break.entity';

@Injectable()
export class MigrationHelperService {

    constructor(
        @InjectRepository(LineEntity) private lineEnt: Repository<LineEntity>,
        @InjectRepository(MigrationLogEntity) private migLogEnt: Repository<MigrationLogEntity>,
        @InjectRepository(UnitEntity) private unitEnt: Repository<UnitEntity>,
        @InjectRepository(UnitDayHourEntity) private unitDayHoursEnt: Repository<UnitDayHourEntity>,
        @InjectRepository(UnitBreakEntity) private unitBreakEnt: Repository<UnitBreakEntity>,
        @InjectRepository(UnitDayBreakEntity) private unitDayBreakEnt: Repository<UnitDayBreakEntity>,
    ) {
        
    }


    async checkIfTableAlreadyMigratedForDayHour(lineId: string, unit: string, tableName: AppTableNamesEnum, date: string, hour: number): Promise<boolean> {
        // const d = date.toString();
        const record = await this.migLogEnt.findOne({ where: { unit: unit, lineId: lineId, tableName: tableName, migrationDate: date, hour: hour, status: MigrationStatusEnum.COMPLETED}});
        return record ? true : false;
    }

    async createTableMigratingLog(unit: string, tableName: AppTableNamesEnum, date: string, hour: number): Promise<boolean> {
        const ent = new MigrationLogEntity();
        ent.hour = hour;
        ent.unit = unit;
        ent.tableName = tableName;
        ent.migrationDate = date;
        await this.migLogEnt.save(ent, {reload: false});
        return true;
    }

    async updateMigrationLog(lineId: string, unit: string, tableName: AppTableNamesEnum, date: string, hour: number, status: number, failReason?: string): Promise<boolean> {
        if(failReason) {
            await this.migLogEnt.update({ unit: unit, lineId: lineId, migrationDate: date, hour: hour, tableName: tableName }, { status: status, failReason: failReason});
        } else {
            await this.migLogEnt.update({ unit: unit, lineId: lineId, migrationDate: date, hour: hour, tableName: tableName }, { status: status});
        }
        return true;
    }

    // Get the unit working hours for the day
    async getUnitActualWorkingHoursForDay(unitId: string, date: string): Promise<number[]> {
        const unitWorkingHours: number[] = [];
        let workingHoursResult = [];
        // first get the unit houts for the specific day 
        const workingHoursRec = await this.unitDayHoursEnt.findOne({ select:['actualWorkingHours'], where: { unitId: unitId, date: date}});
        if(workingHoursRec) {
            workingHoursResult = workingHoursRec.actualWorkingHours?.split(',');
        } else {
            // if no special working hours found for the date, then get the default one for the plant
            const workingHoursRec = await this.unitEnt.findOne({ select:['actualWorkingHours'], where: { unitId: unitId }});
            if(!workingHoursRec) {
                workingHoursResult = [];
            }
            workingHoursResult = workingHoursRec.actualWorkingHours?.split(',');
        }
        workingHoursResult?.forEach(hr => {
            unitWorkingHours.push(Number(hr));
        });
        return unitWorkingHours;
    }

     // Get the unit working hours for the day
     async getUnitConfiguredWorkingHoursForDay(unitId: string, date: string): Promise<number[]> {
        const unitWorkingHours: number[] = [];
        let workingHoursResult = [];
        // first get the unit houts for the specific day 
        const workingHoursRec = await this.unitDayHoursEnt.findOne({ select:['workingHours'], where: { unitId: unitId, date: date}});
        if(workingHoursRec) {
            workingHoursResult = workingHoursRec.workingHours?.split(',');
        } else {
            // if no special working hours found for the date, then get the default one for the plant
            const workingHoursRec = await this.unitEnt.findOne({ select:['workingHours'], where: { unitId: unitId }});
            if(!workingHoursRec) {
                workingHoursResult = [];
            }
            workingHoursResult = workingHoursRec.workingHours?.split(',');
        }
        workingHoursResult?.forEach(hr => {
            unitWorkingHours.push(Number(hr));
        });
        return unitWorkingHours;
    }

    async getFirstWorkingHourInUnitForDate(unitId: string, date: string): Promise<number> {
        const unitWorkingHours = await this.getUnitActualWorkingHoursForDay(unitId, date);
        return unitWorkingHours[0];
    }

    async getLastWorkingHourInUnitForDate(unitId: string, date: string): Promise<number> {
        const unitWorkingHours = await this.getUnitActualWorkingHoursForDay(unitId, date);
        return unitWorkingHours[unitWorkingHours.length - 1];
    }

    // returns the map of hour => total break minutes in that hour
    async getUnitBreakTimesForDay(unitId: string, date: string): Promise<Map<number, number>> {
        const hourBreakMap = new Map<number, number>();
        let breakTimes: {breakTimeFrom: string, breakTimeTo: string, breakMinutes: string}[] = [];
        breakTimes = await this.unitDayBreakEnt.find({select: ['breakTimeFrom', 'breakTimeTo', 'breakMinutes'], where: { unitId: unitId, date: date }});
        if(breakTimes.length == 0) {
            // get the default break times from the unit break
            breakTimes = await this.unitBreakEnt.find({select: ['breakTimeFrom', 'breakTimeTo', 'breakMinutes'], where: { unitId: unitId }});
        }
        breakTimes.forEach(b => {
            const fromStr = b.breakTimeFrom;
            const toStr = b.breakTimeTo;
            const from = fromStr.split(':');
            const to = toStr.split(':');
            const fromHr = Number(from[0]);
            const fromMin = Number(from[1]);
            const toHr = Number(to[0]);
            const toMin = Number(to[1]);
            // begin from the from hour and iterate until it reaches to the to hour
            let currentHour = fromHr;
            do {
                // set the default value to 0
                if(!hourBreakMap.has(currentHour)) {
                    hourBreakMap.set(currentHour, 0);
                }

                let currFromMin = fromMin;
                let currToMin = toMin;
                // if this is the actual mentioned from hour, then the from minute has to be considered from the DB
                if(currentHour != fromHr) {
                    currFromMin = 0;
                }
                if(currentHour != toHr) {
                    currToMin = 60;
                }
                const preVal = hourBreakMap.get(fromHr);
                hourBreakMap.set(fromHr, preVal + currToMin - currFromMin);
                currentHour++;
            } while (currentHour <= toHr);
        });
        return hourBreakMap;
    }

    // get the Map of hour => working minutes (by removing all the break minutes) of the unit for a given day
    async getUntiPlannedWorkingMinutesPerHoursForDay(unitId: string, date: string): Promise<Map<number, number>> {
        const hourWorkingMinsMap = new Map<number, number>();
        const workingHoursInDay = await this.getUnitConfiguredWorkingHoursForDay(unitId, date);
        const hourlyBreakMap = await this.getUnitBreakTimesForDay(unitId, date);
        workingHoursInDay.forEach(hr => {
            const diffMinutes = 60 - ( hourlyBreakMap.get(hr) ?? 0 ); 
            hourWorkingMinsMap.set(hr, diffMinutes);
        });
        return hourWorkingMinsMap;
    }

    async getAllUnits(): Promise<UnitEntity[]> {
        return await this.unitEnt.find({ where: { isActive: true}});
    }


    checkIfDatesValid(dates: string[]): boolean {
        const currDate = new Date();
        const currYear = currDate.getFullYear();
        const currMon = (currDate.getMonth()+1).toString().padStart(2, '0');
        const currDay = currDate.getDate().toString().padStart(2, '0');

        dates.forEach(d => {
            const parts = d.split('-');
            const year = parts[0];
            const month = parts[1];
            const day = parts[2];
            if(Number(month) > 12 || Number(day) > 31) {
                throw new ErrorResponse(0, 'Improper dates are provided');
            }
            if(Number(year) < Number(currYear) ) {
                throw new ErrorResponse(0, 'Previous years are not accepted');
            }
            // console.log(currYear.toString()+currMon.toString()+currDay.toString());
            // console.log(year.toString()+month.toString()+day.toString());
            if( Number(currYear.toString()+currMon.toString()+currDay.toString()) > Number(year.toString()+month.toString()+day.toString()) ) {
                throw new ErrorResponse(0, 'Previous dates are not accepted');
            }
        });
        return true;
    }
}
